import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { use as echartsUse, init as echartsInit, registerTheme as echartsRegisterTheme } from 'echarts/core';
import { PieChart as echartsPieChart } from 'echarts/charts';

import { TooltipComponent, GridComponent, LegendComponent, TitleComponent } from 'echarts/components';

import { SVGRenderer } from 'echarts/renderers';
import { PieChartProps } from './models';
import theme from '../theme.json';
import { convert } from './utils';
import styled from 'styled-components';
import * as styles from './styles';
import { useIsMobile } from '../../../helpers/hooks';
import { tooltipFormatter } from '../utils';

echartsRegisterTheme('sunbit', theme);

echartsUse([TooltipComponent, TitleComponent, LegendComponent, GridComponent, echartsPieChart, SVGRenderer]);

export const PieChart = React.memo(
  styled(({ className, metadata, data, seriesProp = {}, formatter = (value) => value }: PieChartProps) => {
    const chart = useRef(null);
    const PieChartRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const isDataExists = useMemo(() => !!data, [data]);

    const disclaimerKey = useMemo(() => {
      if (!metadata.disclaimer) return null;
      return metadata.disclaimer.value.match(/{{(.*?)}}/)?.[1];
    }, [metadata.disclaimer]);

    const note = useMemo(() => {
      if (!metadata.disclaimer) return null;
      if (!disclaimerKey) return metadata.disclaimer;
      const value = data?.[disclaimerKey];
      if (!value) return null;
      return metadata.disclaimer.value.replace(`{{${disclaimerKey}}}`, value);
    }, [data, disclaimerKey, metadata.disclaimer]);

    const options = useMemo(() => {
      const converted = convert(data, metadata);
      const sum = converted.series[0].data.reduce((acc, v) => acc + v.value, 0);
      const series = converted.series.map((serie) => ({
        ...serie,
        type: 'pie',
        radius: ['45%', '70%'],
        label: {
          show: true,
          position: 'inside',
          color: 'white',
          fontSize: 16,
          fontWeight: 500,
          formatter: ({ value }: { value: number }) =>  !value ? '' : formatter(value, metadata.format),
        },
        left: 0,
        labelLine: {
          show: false,
        },
        center: isMobile ? ['50%', '50%'] : ['30%', '50%'],
        ...seriesProp,
      }));

      return {
        grid: { containLabel: true },
        tooltip: {
          trigger: 'item',
          formatter: (params) => tooltipFormatter(params, (value: number) => formatter(value, metadata.format)),
        },

        title: {
          show: metadata.title,
          text: sum,
          subtext: `${metadata.title}`,
          textAlign: 'center',
          textStyle: {
            fontSize: 30,
            fontFamily: 'Poppins',
          },
          subtextStyle: {
            fontSize: 18,
            fontFamily: 'Poppins',
            lineHeight: 26,
          },
          left: isMobile ? 'middle' : '29%',
          top: '40%',
        },
        legend: {
          orient: isMobile ? 'horizontal' : 'vertical',
          top: isMobile ? 0 : 'center',
          itemWidth: 15,
          itemGap: 20,
          icon: 'circle',
          textStyle: {
            fontFamily: 'Poppins',
            overflow: 'break',
          },

          left: isMobile ? 'center' : '60%',
          formatter: (name: string) => {
            const { value, key } = converted.series[0].data.find((d) => d.name === name);
            const hasDisclaimer = key === metadata.disclaimer?.dependedOn;
            const label = name.replace(/\(.*\)/, `\n${name.match(/\(.*\)/)}`);
            return `${formatter(value, metadata.format)} ${label}${hasDisclaimer ? ' *' : ''}`;
          },
        },
        series: series,
      };
    }, [data, metadata, metadata.disclaimer, isMobile]);

    useEffect(() => {
      let timeout;
      if (PieChartRef.current && !chart.current) {
        chart.current = echartsInit(PieChartRef.current, 'sunbit');
      }
      if (chart.current) {
        if (!isDataExists) {
          chart.current.showLoading();
        } else {
          chart.current.hideLoading();
          chart.current.setOption(options);
        }
        timeout = setTimeout(() => chart.current.resize(), 1000); // resizing after layout shifts are done hopefully
      }
      return () => {
        if (chart.current) {
          chart.current.dispose();
          chart.current = null;
        }
        if (timeout) clearTimeout(timeout);
      };
    }, [PieChartRef, options]);

    useLayoutEffect(() => {
      const updateSize = (): void => {
        if (chart.current) {
          chart.current.resize();
        }
      };
      window.addEventListener('resize', updateSize);
      return (): void => window.removeEventListener('resize', updateSize);
    }, []);

    return (
      <div className={className}>
        <span className="pie_title">{metadata.description}</span>
        <div className="pie" ref={PieChartRef}></div>
        {note && (
          <div className="note">
            <span>*</span>
            {note}
          </div>
        )}
      </div>
    );
  })`
    ${styles.PieChart}
  `
);
